import { useState, useEffect } from "react";
// import { motion } from "framer-motion";
import { Badge } from "src/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import {
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  Clock,
  GraduationCap,
  DollarSign,
  Building,
  User,
  Hash,
  CalendarDays,
  RefreshCw,
  Bookmark,
  Tag,
} from "lucide-react";
import { Avatar } from "antd";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../lib/api";
import { toast } from "sonner";
import { Skeleton } from "src/components/ui/skeleton";
import { motion } from "framer-motion";
import { formatEGP } from "../lib/utils";

export function UserProfile() {
  const { bid } = useParams();

  const [mounted, setMounted] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [user, setUser] = useState([]);

  const handleFetchUser = async () => {
    try {
      setLoadingUser(true);
      const { data } = await axios.get(`${BaseUrlApi}/users/${bid}`);
      setUser(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingUser(false);
    }
  };

  useEffect(() => {
    if (bid) handleFetchUser();
  }, [bid]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  // Format date to Arabic format
  const formatDate = (dateString) => {
    if (!dateString) return "غير متوفر";

    const date = new Date(dateString);
    return new Intl.DateTimeFormat("ar-EG", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const translateRole = (role) => {
    if (role === "admin") return "مسؤول";
    if (role === "user") return "موظف";
    if (role === "accountant") return "محاسب";
    if (role === "marketer") return "مسوق";
    return role;
  };

  if (loadingUser) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        dir="rtl"
        className="space-y-5"
      >
        <div className="space-y-6" dir="rtl">
          {/* Profile Header Skeleton */}
          <div className="animate-pulse">
            <div className="rounded-xl overflow-hidden p-6 shadow">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-mainColor to-secondaryColor rounded-full blur-xl opacity-30"></div>
                  <Skeleton className="h-32 w-32 rounded-full border-4 border-background" />
                </div>

                <div className="text-center md:text-right space-y-2">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-6 w-24" />
                  <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-4">
                    <Skeleton className="h-6 w-28 rounded-full" />
                    <Skeleton className="h-6 w-28 rounded-full" />
                    <Skeleton className="h-6 w-28 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Work Details Skeleton */}
          <div className="animate-pulse">
            <div className="rounded-xl overflow-hidden shadow">
              <div className="bg-gradient-to-r from-secondaryColor/20 to-transparent p-4">
                <Skeleton className="h-6 w-32" />
              </div>
              <div className="p-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            </div>
          </div>

          {/* Qualifications & Salary Skeleton */}
          <div className="grid gap-6 md:grid-cols-2">
            <div className="animate-pulse">
              <div className="rounded-xl overflow-hidden shadow">
                <div className="bg-gradient-to-r from-secondaryColor/20 to-transparent p-4">
                  <Skeleton className="h-6 w-32" />
                </div>
                <div className="p-6">
                  <Skeleton className="h-6 w-full" />
                </div>
              </div>
            </div>
            <div className="animate-pulse">
              <div className="rounded-xl overflow-hidden shadow">
                <div className="bg-gradient-to-r from-secondaryColor/20 to-transparent p-4">
                  <Skeleton className="h-6 w-32" />
                </div>
                <div className="p-6">
                  <Skeleton className="h-6 w-full" />
                </div>
              </div>
            </div>
          </div>

          {/* Additional Details Skeleton */}
          <div className="animate-pulse">
            <div className="rounded-xl overflow-hidden shadow">
              <div className="bg-gradient-to-r from-secondaryColor/20 to-transparent p-4">
                <Skeleton className="h-6 w-32" />
              </div>
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
      className="space-y-5"
    >
      {/* Profile Header */}
      <div>
        <Card className="overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <div className="relative">
                <Avatar
                  shape="circle"
                  className="h-32 w-32 border-4 border-background shadow-md"
                  src={`${UrlImage}${user?.image}`}
                >
                  {user?.name?.charAt(0)}{" "}
                </Avatar>
              </div>

              <div className="text-center md:text-right space-y-2">
                <h1 className="text-3xl font-bold">{user?.name}</h1>
                <Badge
                  variant="outline"
                  className="capitalize text-sm px-3 py-1 bg-secondaryColor/20 text-mainColor border-secondaryColor/30"
                >
                  {translateRole(user?.role) || "غير موجود"}
                </Badge>

                <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-4">
                  <InfoBadge
                    icon={<Mail className="h-4 w-4" />}
                    value={user?.email || "غير موجود"}
                  />
                  <InfoBadge
                    icon={<Phone className="h-4 w-4" />}
                    value={user?.phone || "غير موجود"}
                  />
                  <InfoBadge
                    icon={<MapPin className="h-4 w-4" />}
                    value={user?.address || "غير موجود"}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Work Details */}
      <div>
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-secondaryColor/20 to-transparent">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Briefcase className="h-5 w-5 text-mainColor" />
              معلومات العمل
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <DetailCard
                icon={<Building className="h-5 w-5 text-mainColor" />}
                title="مكان العمل"
                value={
                  (user?.work_place === "remote"
                    ? "عن بعد"
                    : user?.work_place === "onsite" && "في المكتب") ||
                  "غير موجود"
                }
              />
              <DetailCard
                icon={<Calendar className="h-5 w-5 text-mainColor" />}
                title="أيام العمل"
                value={
                  user?.work_days
                    ? `${user?.work_days} أيام في الأسبوع`
                    : "غير موجود"
                }
              />
              <DetailCard
                icon={<Clock className="h-5 w-5 text-mainColor" />}
                title="ساعات العمل"
                value={
                  user?.work_hours
                    ? `${user?.work_hours} ساعات في اليوم`
                    : "غير موجود"
                }
              />
              <DetailCard
                icon={<Tag className="h-5 w-5 text-mainColor" />}
                title="القسم"
                value={`${
                  user?.category[0]?.title
                    ? user?.category[0]?.title
                    : "غير موجود"
                }`}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Qualifications & Salary */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-secondaryColor/20 to-transparent">
            <CardTitle className="flex items-center gap-2 text-xl">
              <GraduationCap className="h-5 w-5 text-mainColor" />
              المؤهلات
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-lg">{user?.qualification || "غير موجود"}</p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-secondaryColor/20 to-transparent">
            <CardTitle className="flex items-center gap-2 text-xl">
              <DollarSign className="h-5 w-5 text-mainColor" />
              الراتب
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-lg">{formatEGP(user?.salary) || 0} شهرياً</p>
          </CardContent>
        </Card>
      </div>

      {/* Additional Details */}
      <div>
        <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-secondaryColor/20 to-transparent">
            <CardTitle className="flex items-center gap-2 text-xl">
              <User className="h-5 w-5 text-mainColor" />
              معلومات إضافية
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              <InfoCard
                icon={<Hash className="h-5 w-5 text-mainColor" />}
                title="رقم المستخدم"
                value={user?.id?.toString() || "غير موجود"}
              />
              <InfoCard
                icon={<Bookmark className="h-5 w-5 text-mainColor" />}
                title="رقم العرض"
                value={user?.bid?.toString() || "غير موجود"}
              />
              <InfoCard
                icon={<CalendarDays className="h-5 w-5 text-mainColor" />}
                title="تاريخ الإنشاء"
                value={
                  user?.created_at ? formatDate(user?.created_at) : "غير موجود"
                }
              />
              <InfoCard
                icon={<RefreshCw className="h-5 w-5 text-mainColor" />}
                title="آخر تحديث"
                value={
                  user?.updated_at ? formatDate(user?.updated_at) : "غير موجود"
                }
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
}

function InfoBadge({ icon, value }) {
  return (
    <div className="flex items-center gap-2 rounded-full bg-background border border-secondaryColor/30 px-3 py-1 text-sm shadow-sm hover:border-mainColor/50">
      {icon}
      <span>{value}</span>
    </div>
  );
}

function DetailCard({ icon, title, value }) {
  return (
    <div className="rounded-xl border border-secondaryColor/30 p-4 transition-all hover:shadow-md hover:border-mainColor/50 bg-background">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h3 className="font-medium">{title}</h3>
      </div>
      <p className="text-muted-foreground">{value}</p>
    </div>
  );
}

function InfoCard({ icon, title, value }) {
  return (
    <div className="rounded-xl border border-secondaryColor/30 p-4 transition-all hover:shadow-md hover:border-mainColor/50 bg-background">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h3 className="font-medium">{title}</h3>
      </div>
      <p className="text-muted-foreground">{value}</p>
    </div>
  );
}
